import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaBook, FaShoppingCart } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import { API_BASE_URL } from '../../utils/constants';

function CourseCard({ course, isPurchased = false, bundleMode = false, isSelected = false, onToggleSelection = null }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [imgError, setImgError] = useState(false);

  const thumbnailUrl = `${API_BASE_URL}/api/courses/${course.id}/thumbnail`;

  const handlePurchase = async () => {
    if (!user) {
      navigate('/login', { state: { from: `/course/${course.id}` } });
      return;
    }
    setPurchasing(true);
    try {
      const { checkoutUrl } = await apiService.createCheckout(course.id);
      window.location.href = checkoutUrl;
    } catch (err) {
      alert(err.message || 'فشلت عملية إنشاء الدفع. يرجى المحاولة لاحقاً.');
      console.error('Checkout error:', err);
      setPurchasing(false);
    }
  };

  const handleBundleToggle = () => {
    if (onToggleSelection) {
      onToggleSelection(course);
    }
  };
  
  const progressPercentage = course.current_page && course.pages_count 
    ? Math.round((course.current_page / course.pages_count) * 100) 
    : 0;

  return (
    <div className={`bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative ${bundleMode ? 'cursor-pointer' : ''} ${isSelected ? 'ring-4 ring-primary' : ''}`}
         onClick={bundleMode ? handleBundleToggle : undefined}>
      {/* Bundle Mode Checkbox */}
      {bundleMode && (
        <div className="absolute top-4 left-4 z-20">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg transition-all ${isSelected ? 'bg-primary' : 'bg-white'}`}>
            {isSelected ? (
              <i className="fas fa-check text-white"></i>
            ) : (
              <i className="fas fa-plus text-gray-400"></i>
            )}
          </div>
        </div>
      )}
      <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
        {!imgError && course.summary_image_url ? (
          <>
            <img
              src={thumbnailUrl}
              alt={course.title_ar}
              className="w-full h-full object-cover"
              onError={() => setImgError(true)}
            />
            {/* Branding banner overlay */}
            <div className="absolute bottom-0 left-0 right-0" style={{
              background: 'linear-gradient(to right, rgba(0,52,128,0.88), rgba(0,82,204,0.88), rgba(0,52,128,0.88))',
            }}>
              <div className="flex items-center justify-between px-2.5 py-1">
                <div className="flex items-center gap-1">
                  <img src="/icon.png" alt="Allemni" className="w-4 h-4 rounded" />
                  <span className="text-white font-bold text-[9px]">Allemni</span>
                  <span className="text-white/60 text-[7px]">علمني</span>
                </div>
                <span className="text-white/50 text-[7px]">allemni.site</span>
              </div>
            </div>
          </>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary via-blue-600 to-indigo-700 flex flex-col justify-end p-6 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-white to-transparent rounded-full blur-3xl"></div>
            </div>
            <div className="relative z-10 mb-4">
              <h3 className="text-white font-black text-xl mb-2 leading-tight">{course.title_ar}</h3>
              <p className="text-white/70 text-sm font-medium">محتوى تعليمي بالدارجة</p>
            </div>
            <div className="relative z-10 flex justify-between items-center pt-4 border-t border-white/10">
              <div className="text-white/60 text-xs font-medium">{course.pages_count} صفحة</div>
              <div className="text-white text-sm font-bold">علمني</div>
            </div>
          </div>
        )}
        <div className="absolute top-2 right-2 md:top-4 md:right-4 bg-white px-3 py-1.5 md:px-4 md:py-2 rounded-full shadow-lg">
          <span className="text-primary font-bold text-sm md:text-lg">{course.price} دج</span>
        </div>
      </div>
      
      <div className="p-4 md:p-6">
        <div className="flex items-center gap-1 md:gap-2 mb-2 md:mb-3">
          <i className={`${course.category_icon} text-primary text-xs md:text-base`}></i>
          <span className="text-xs md:text-sm text-gray-600">{course.category_name}</span>
        </div>
        
        <h3 className="text-sm md:text-xl font-bold text-gray-800 mb-2 md:mb-3 line-clamp-2">
          {course.title_ar}
        </h3>
        
        <p className="text-gray-600 text-xs md:text-sm mb-3 md:mb-4 line-clamp-2 md:line-clamp-3">
          {course.description_ar}
        </p>
        
        <div className="flex items-center gap-2 md:gap-4 text-xs md:text-sm text-gray-600 mb-3 md:mb-4 flex-wrap">
          <span className="flex items-center gap-1">
            <i className="fas fa-video text-purple-600"></i>
            <span className="hidden md:inline">فيديو</span>
          </span>
          <span className="flex items-center gap-1">
            <i className="fas fa-book-open text-blue-600"></i>
            <span className="hidden md:inline">محتوى</span>
          </span>
          <span className="flex items-center gap-1">
            <i className="fas fa-presentation text-green-600"></i>
            <span className="hidden md:inline">عرض</span>
          </span>
          <span className="flex items-center gap-1">
            <i className="fas fa-star text-orange-600"></i>
            <span className="hidden md:inline">بطاقات</span>
          </span>
        </div>

        {isPurchased && (
          <div className="mb-3 md:mb-4">
            <div className="flex justify-between text-xs md:text-sm mb-2">
              <span className="text-gray-600">التقدم</span>
              <span className="text-primary font-bold">{progressPercentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
          </div>
        )}
        
        {isPurchased ? (
          <Link
            to={`/course/${course.id}/view`}
            className="block w-full text-center bg-primary text-white px-4 py-2 md:px-6 md:py-3 rounded-xl font-bold text-sm md:text-base hover:bg-primary-dark transition-all duration-300"
          >
            {course.completed ? 'مراجعة' : progressPercentage > 0 ? 'استكمل' : 'ابدأ التعلم'}
          </Link>
        ) : bundleMode ? (
          <div className="text-center py-2">
            <span className={`font-bold text-sm ${isSelected ? 'text-primary' : 'text-gray-500'}`}>
              {isSelected ? '✓ محدد' : 'اضغط للإضافة'}
            </span>
          </div>
        ) : (
          <>
            <button
              onClick={() => setShowPurchaseModal(true)}
              className="block w-full text-center bg-primary text-white px-4 py-2 md:px-6 md:py-3 rounded-xl font-bold text-sm md:text-base hover:bg-primary-dark transition-all duration-300"
            >
              <FaShoppingCart className="inline ml-1 md:ml-2 text-xs md:text-base" />
              <span className="hidden md:inline">اشترِ الدورة الآن</span>
              <span className="md:hidden">اشترِ الآن</span>
            </button>
            <Link
              to={`/course/${course.id}`}
              className="block w-full text-center bg-white text-primary border-2 border-primary px-4 py-2 md:px-6 md:py-3 rounded-xl font-bold text-sm md:text-base hover:bg-primary hover:text-white transition-all duration-300 mt-2 md:mt-3"
            >
              <FaBook className="inline ml-1 md:ml-2 text-xs md:text-base" />
              <span className="hidden md:inline">معاينة محتوى الدورة</span>
              <span className="md:hidden">معاينة</span>
            </Link>
          </>
        )}
      </div>

      {showPurchaseModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3"
          onClick={() => setShowPurchaseModal(false)}
        >
          <div 
            className="bg-white rounded-2xl md:rounded-3xl p-4 md:p-8 max-w-[500px] w-full shadow-2xl relative max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowPurchaseModal(false)}
              className="absolute top-2 left-2 md:top-4 md:left-4 text-gray-400 hover:text-gray-600 text-xl md:text-2xl w-8 h-8 flex items-center justify-center transition-colors z-10"
            >
              <i className="fas fa-times"></i>
            </button>

            <h2 className="text-lg md:text-3xl font-black text-gray-800 mb-3 md:mb-6 pr-8">تأكيد الشراء</h2>
            
            <div className="bg-gray-50 p-3 md:p-6 rounded-xl md:rounded-2xl mb-3 md:mb-6">
              <div className="flex justify-between items-start gap-2 mb-2 md:mb-3">
                <span className="text-xs md:text-base text-gray-600 whitespace-nowrap">الدورة:</span>
                <span className="font-bold text-xs md:text-base text-gray-800 text-right flex-1">{course.title_ar}</span>
              </div>
              <div className="border-t pt-2 md:pt-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs md:text-base text-gray-600 font-bold">المجموع:</span>
                  <span className="font-black text-primary text-xl md:text-3xl">{course.price} دج</span>
                </div>
              </div>
            </div>

            <div className="mb-3 md:mb-6">
              <button
                onClick={handlePurchase}
                disabled={purchasing}
                className="w-full bg-primary text-white px-3 py-2.5 md:px-6 md:py-4 rounded-xl font-bold hover:bg-primary-dark transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2 text-xs md:text-base"
              >
                <i className="fas fa-credit-card text-sm md:text-base"></i>
                <span>{purchasing ? 'جاري التحويل...' : 'دفع أونلاين'}</span>
              </button>
              <p className="text-[10px] md:text-xs text-gray-600 text-center mt-1 md:mt-2 leading-tight">
                سيتم تحويلك لصفحة الدفع الآمنة<br className="md:hidden" /> (CIB / EDAHABIA)
              </p>
            </div>

            <div className="relative my-3 md:my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-xs md:text-sm text-gray-400">أو</span>
              </div>
            </div>

            <div>
              <button
                onClick={() => {
                  setShowPurchaseModal(false);
                  navigate(`/manual-payment/${course.id}`);
                }}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-2.5 md:px-6 md:py-4 rounded-xl font-bold hover:from-green-600 hover:to-green-700 transition-all duration-300 flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 text-xs md:text-base leading-tight"
              >
                <div className="flex items-center gap-2">
                  <i className="fas fa-money-bill-wave text-sm md:text-base"></i>
                  <span>الدفع اليدوي عبر CCP</span>
                </div>
                <span className="text-[10px] md:text-sm opacity-90">(حوالة بريدية)</span>
              </button>
              <p className="text-[10px] md:text-xs text-gray-600 text-center mt-1 md:mt-2">
                للدفع عبر الحساب الجاري البريدي
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CourseCard;
